import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Pencil, Trash2, Search, ChevronLeft, ChevronRight, LogIn, Ban, CheckCircle2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { createUser, deleteUser, fetchUsers, getUser, setUserActive, type User, updateUser } from "@/lib/usersApi";
import { useDebounce } from "@/hooks/useDebounce";

const createUserSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  password: z.string().min(8, "Password must be at least 8 characters"),
  phoneNumber: z.string().optional(),
  department: z.enum(["sales", "operations", "finance"]).optional(),
  role: z.enum(["ceo", "general_manager", "sales_staff", "operations_staff", "finance_staff"]),
});

type CreateUserFormData = z.infer<typeof createUserSchema>;

const CreateUser = () => {
  const navigate = useNavigate();
  const { userRole, user: currentUser, loginAs } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(null);
  const [togglingActiveUserId, setTogglingActiveUserId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const totalPages = Math.ceil(totalCount / pageSize);

  const form = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      role: "sales_staff",
    },
  });

  const editForm = useForm<{
    password?: string;
    phoneNumber?: string;
    department?: string;
    role?: string;
  }>({
    defaultValues: {},
  });

  const selectedRole = form.watch("role");
  const showDepartment = selectedRole && ["sales_staff", "operations_staff", "finance_staff"].includes(selectedRole);

  useEffect(() => {
    if (userRole !== "ceo" && userRole !== "general_manager") {
      navigate("/dashboard");
    }
  }, [userRole, navigate]);

  useEffect(() => {
    if (userRole === "ceo" || userRole === "general_manager") {
      fetchUsersList();
    }
  }, [userRole, currentPage, pageSize, debouncedSearchQuery]);

  const fetchUsersList = async () => {
    try {
      const payload = await fetchUsers({
        page: currentPage,
        size: pageSize,
        search: debouncedSearchQuery || undefined,
        sortField: "created_at",
        sortOrder: "desc",
      });
      setUsers(payload.items || []);
      setTotalCount(payload.total || 0);
    } catch (error: any) {
      console.error("Error fetching users:", error);
    }
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const onSubmit = async (data: CreateUserFormData) => {
    setLoading(true);

    try {
      await createUser({
        email: data.email || null,
        password: data.password,
        fullName: data.fullName,
        phoneNumber: data.phoneNumber,
        department: showDepartment ? data.department : null,
        role: data.role,
      });

      toast({
        title: "User created successfully",
        description: `${data.fullName} has been added to the system.`,
      });

      form.reset({
        fullName: "",
        email: "",
        password: "",
        phoneNumber: "",
        department: undefined,
        role: undefined as any,
      });
      fetchUsersList();
    } catch (error: any) {
      toast({
        title: "Error creating user",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const onEditSubmit = async (data: any) => {
    if (!editingUser) return;
    setEditLoading(true);

    try {
      await updateUser(editingUser.id, {
        password: data.password || undefined,
        phoneNumber: data.phoneNumber ?? null,
        department: data.department || null,
        role: data.role,
      });

      toast({
        title: "User updated successfully",
      });

      setEditingUser(null);
      editForm.reset();
      fetchUsersList();
    } catch (error: any) {
      toast({
        title: "Error updating user",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setEditLoading(false);
    }
  };

  const handleEdit = async (user: User) => {
    try {
      const fullUser = await getUser(user.id);
      setEditingUser(fullUser);
      editForm.reset({
        phoneNumber: fullUser.phone_number || "",
        department: fullUser.department || undefined,
        role: fullUser.role,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!deletingUser) return;

    setDeleteLoading(true);

    try {
      await deleteUser(deletingUser.id);

      toast({
        title: "User deleted successfully",
        description: `${deletingUser.full_name} has been removed from the system.`,
      });

      setDeletingUser(null);
      fetchUsersList();
    } catch (error: any) {
      toast({
        title: "Error deleting user",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const getRoleLabel = (role: string) => {
    const roleMap: Record<string, string> = {
      ceo: "CEO",
      general_manager: "General Manager",
      sales_staff: "Sales Employee",
      operations_staff: "Operations Employee",
      finance_staff: "Finance Employee",
    };
    return roleMap[role] || role;
  };

  const canSeeActions = userRole === "ceo" || userRole === "general_manager";

  const handleLoginAs = async (targetUser: User) => {
    if (userRole === "general_manager" && targetUser.role === "ceo") {
      toast({
        title: "Action not allowed",
        description: "General Manager cannot login as CEO.",
        variant: "destructive",
      });
      return;
    }

    if (currentUser?.id === targetUser.id) {
      return;
    }

    setImpersonatingUserId(targetUser.id);
    const { error } = await loginAs(targetUser.id);
    if (error) {
      toast({
        title: "Error logging in as user",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    }
    setImpersonatingUserId(null);
  };

  const handleToggleActive = async (targetUser: User) => {
    if (currentUser?.id === targetUser.id) {
      return;
    }
    if (userRole === "general_manager" && targetUser.role === "ceo" && targetUser.active !== false) {
      toast({
        title: "Action not allowed",
        description: "General Manager cannot disable a CEO.",
        variant: "destructive",
      });
      return;
    }

    const nextActive = targetUser.active === false;
    setTogglingActiveUserId(targetUser.id);
    try {
      await setUserActive(targetUser.id, nextActive);
      setUsers((prev) =>
        prev.map((u) => (u.id === targetUser.id ? { ...u, active: nextActive } : u))
      );
      toast({
        title: nextActive ? "User enabled" : "User disabled",
        description: nextActive
          ? `${targetUser.full_name} can log in again.`
          : `${targetUser.full_name} can no longer log in.`,
      });
    } catch (error: any) {
      toast({
        title: "Error updating user",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setTogglingActiveUserId(null);
    }
  };


  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Create User</h1>
          <p className="text-muted-foreground">
            Add a new user to the system
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Role and Permissions</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem className={!showDepartment ? "md:col-span-2" : undefined}>
                      <FormLabel>Role/Permission Level *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {userRole === "ceo" && (
                            <SelectItem value="ceo">CEO</SelectItem>
                          )}
                          <SelectItem value="general_manager">General Manager</SelectItem>
                          <SelectItem value="sales_staff">Sales Employee</SelectItem>
                          <SelectItem value="operations_staff">Operations Employee</SelectItem>
                          <SelectItem value="finance_staff">Finance Employee</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {showDepartment && (
                  <FormField
                    control={form.control}
                    name="department"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Department *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select department" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="sales">Sales</SelectItem>
                            <SelectItem value="operations">Operations/Concierge</SelectItem>
                            <SelectItem value="finance">Finance</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>User Information</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="john.doe@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password *</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Minimum 8 characters"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input placeholder="+971 50 123 4567" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/dashboard")}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create User
              </Button>
            </div>
          </form>
        </Form>

        {(userRole === "ceo" || userRole === "general_manager") && (
          <Card className="mt-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Registered Users</CardTitle>
                <div className="relative w-72">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchQuery ? `No users found matching "${searchQuery}"` : "No users found"}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Full Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created At</TableHead>
                      {canSeeActions && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((listedUser) => (
                    <TableRow key={listedUser.id}>
                      <TableCell className="font-medium">{listedUser.full_name}</TableCell>
                      <TableCell>{listedUser.email || "-"}</TableCell>
                      <TableCell>{listedUser.phone_number || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{getRoleLabel(listedUser.role)}</Badge>
                      </TableCell>
                      <TableCell>
                        {listedUser.department ? (
                          <span className="capitalize">{listedUser.department}</span>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {listedUser.active === false ? (
                          <Badge variant="destructive">Disabled</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(listedUser.created_at).toLocaleDateString()}
                      </TableCell>
                      {canSeeActions && (
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              title={
                                currentUser?.id === listedUser.id
                                  ? "You are already using this user"
                                  : userRole === "general_manager" && listedUser.role === "ceo"
                                  ? "General Manager cannot login as CEO"
                                  : "Login as this user"
                              }
                              disabled={
                                impersonatingUserId !== null ||
                                currentUser?.id === listedUser.id ||
                                (userRole === "general_manager" && listedUser.role === "ceo")
                              }
                              onClick={() => handleLoginAs(listedUser)}
                            >
                              {impersonatingUserId === listedUser.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <LogIn className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title={
                                currentUser?.id === listedUser.id
                                  ? "You cannot disable your own account"
                                  : userRole === "general_manager" && listedUser.role === "ceo" && listedUser.active !== false
                                  ? "General Manager cannot disable a CEO"
                                  : listedUser.active === false
                                  ? "Enable this user"
                                  : "Disable this user"
                              }
                              disabled={
                                togglingActiveUserId !== null ||
                                currentUser?.id === listedUser.id ||
                                (userRole === "general_manager" && listedUser.role === "ceo" && listedUser.active !== false)
                              }
                              onClick={() => handleToggleActive(listedUser)}
                              className={listedUser.active === false ? "text-emerald-700 hover:text-emerald-800" : "text-destructive hover:text-destructive"}
                            >
                              {togglingActiveUserId === listedUser.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : listedUser.active === false ? (
                                <CheckCircle2 className="h-4 w-4" />
                              ) : (
                                <Ban className="h-4 w-4" />
                              )}
                            </Button>
                            {userRole === "ceo" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEdit(listedUser)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setDeletingUser(listedUser)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}

              {totalCount > 0 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-4">
                    <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[70px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="text-sm text-muted-foreground">
                      Showing {totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
                      {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="text-sm">
                      Page {currentPage} of {totalPages || 1}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.min(currentPage + 1, Math.max(totalPages, 1)))}
                      disabled={currentPage >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-4">
                <FormField
                  control={editForm.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input placeholder="+971 50 123 4567" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password (optional)</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Leave empty to keep current"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {editingUser?.role.includes("staff") && (
                  <FormField
                    control={editForm.control}
                    name="department"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Department</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select department" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="sales">Sales</SelectItem>
                            <SelectItem value="operations">Operations/Concierge</SelectItem>
                            <SelectItem value="finance">Finance</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {editingUser?.role !== "ceo" && (
                  <FormField
                    control={editForm.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Role</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="general_manager">General Manager</SelectItem>
                            <SelectItem value="sales_staff">Sales Employee</SelectItem>
                            <SelectItem value="operations_staff">Operations Employee</SelectItem>
                            <SelectItem value="finance_staff">Finance Employee</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <div className="flex justify-end gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingUser(null)}
                    disabled={editLoading}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={editLoading}>
                    {editLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Changes
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deletingUser} onOpenChange={() => setDeletingUser(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the user{" "}
                <strong>{deletingUser?.full_name}</strong> and remove all their data from the system.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleteLoading}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete User
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default CreateUser;
